import "./App.css";

import { Outlet, useLocation } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";

import OrgRedirect from "./components/auth/OrgRedirect";
import PendingPromptHandler from "./components/auth/PendingPromptHandler";
import { Toaster } from "./components/ui/sonner";
import { observer } from "mobx-react-lite";
import { projectService } from "./services/ProjectService";
import { pyodideService } from "./services/PyodideService";
import { rootStore } from "./stores/RootStore";
import { authStore } from "./stores/AuthStore";

const App: React.FC = observer(() => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { uiStore, projectStore } = rootStore;
  const { getToken } = useAuth();
  const location = useLocation();

  // Handle auth token setup
  useEffect(() => {
    if (!isUserLoaded || !user) {
      authStore.reset();
      return;
    }

    const initAuth = async () => {
      try {
        const token = await getToken();
        if (token) {
          authStore.setToken(token);
          sessionStorage.setItem('clerk-auth-token', token);
          
          // Set user context
          const orgId = user.organizationMemberships[0]?.organization.id || null;
          authStore.setUserContext(user.id, orgId);
        }
      } catch (error) {
        console.error("Error initializing auth token:", error);
      }
    };

    initAuth();
  }, [isUserLoaded, user, getToken]);

  // Handle project initialization after token is set
  useEffect(() => {
    if (!isUserLoaded || !user || !authStore.isTokenSet) return;

    // Skip initialization on landing page
    const isLandingPage = location.pathname === '/' || location.pathname === '/home';
    if (isLandingPage) return;

    const init = async () => {
      uiStore.updateLogs("Initializing application...");
      try {
        await projectService.fetchAndCacheProjects();
        const urlProj = projectService.initializeFromURL();
        if (urlProj) {
          await projectStore.loadProject(urlProj, uiStore.updateLogs.bind(uiStore));
          projectStore.setSelectedFile("/project/hello.py");
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        uiStore.updateLogs(`Init error: ${msg}`);
        const url = new URL(window.location.href);
        url.searchParams.delete("project");
        window.history.replaceState({}, "", url.toString());
      }
    };
    init();
  }, [isUserLoaded, user, authStore.isTokenSet, location.pathname, uiStore, projectStore]);

  // Update project fetching to skip on landing page
  useEffect(() => {
    if (!isUserLoaded || !user || !authStore.isTokenSet) return;

    // Skip on landing page
    const isLandingPage = location.pathname === '/' || location.pathname === '/home';
    if (isLandingPage) return;

    projectService.fetchAndCacheProjects();
    const id = setInterval(() => {
      projectService.fetchAndCacheProjects();
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [isUserLoaded, user, authStore.isTokenSet, location.pathname]);

  // Only initialize Pyodide when not on the landing page
  useEffect(() => {
    // Check if we're on the landing page
    const isLandingPage = location.pathname === '/' || location.pathname === '/home';

    // Only set Pyodide timeout when not on landing page
    if (!isLandingPage) {
      const tid = setTimeout(() => {
        if (!pyodideService.isReady()) {
          uiStore.setPyodideTimedOut(true);
        }
      }, 30_000);
      return () => clearTimeout(tid);
    }

    return undefined;
  }, [location.pathname, uiStore]);

  useEffect(() => {
    function toggleAgent() { uiStore.setAgentVisible(!uiStore.agentVisible); }
    function toggleLogs() { uiStore.setLogsVisible(!uiStore.logsVisible); }
    function refreshProjects() { projectService.fetchAndCacheProjects(true); }

    window.addEventListener("toggleAgentPanel", toggleAgent);
    window.addEventListener("toggleLogsPanel", toggleLogs);
    window.addEventListener("refreshProjects", refreshProjects);

    return () => {
      window.removeEventListener("toggleAgentPanel", toggleAgent);
      window.removeEventListener("toggleLogsPanel", toggleLogs);
      window.removeEventListener("refreshProjects", refreshProjects);
    };
  }, []);

  // Check if we're on specific auth pages where OrgRedirect should not run
  const isAuthPath =
    location.pathname === '/dashboard' ||
    location.pathname === '/create-org' ||
    location.pathname === '/sign-in' ||
    location.pathname === '/sign-up' ||
    location.pathname === '/' ||
    location.pathname === '/home' ||
    location.pathname === '/pricing' ||
    location.pathname === '/privacy-policy' ||
    location.pathname === '/terms-and-conditions' ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/bitflip' ||
    location.pathname.startsWith('/settings/organization');

  return (
    <>
      {/* Only render OrgRedirect when not on auth pages */}
      {!isAuthPath && <OrgRedirect />}
      {/* Add PendingPromptHandler to handle pending prompts after sign-up */}
      <PendingPromptHandler />
      <Toaster position="top-right" closeButton />
      <Outlet />
    </>
  );
});

export default App;
